import type { PipSnapConfig } from "./pip-config";
import { DEFAULT_PIP_SNAP_CONFIG } from "./pip-config";

export interface PipBounds {
	width: number;
	height: number;
}

export interface PipPosition {
	x: number;
	y: number;
}

export interface PipDimensions {
	widthPx: number;
	heightPx: number;
}

export interface PipSnapInsets {
	top?: number;
	right?: number;
	bottom?: number;
	left?: number;
}

export function pipPositionsEqual(a: PipPosition, b: PipPosition): boolean {
	return a.x === b.x && a.y === b.y;
}

export function pipDimensionsEqual(
	a: PipDimensions,
	b: PipDimensions,
): boolean {
	return a.widthPx === b.widthPx && a.heightPx === b.heightPx;
}

function distance(a: PipPosition, b: PipPosition): number {
	const dx = a.x - b.x;
	const dy = a.y - b.y;
	return Math.hypot(dx, dy);
}

export function resolvePipSnapPadding(
	config: PipSnapConfig,
	insets?: PipSnapInsets,
): { top: number; right: number; bottom: number; left: number } {
	return {
		top: config.edgePaddingPx + (insets?.top ?? 0),
		right: config.edgePaddingPx + (insets?.right ?? 0),
		bottom: config.edgePaddingPx + (insets?.bottom ?? 0),
		left: config.edgePaddingPx + (insets?.left ?? 0),
	};
}

export function clampPipPosition(params: {
	position: PipPosition;
	bounds: PipBounds;
	pipDimensions: PipDimensions;
	insets?: PipSnapInsets;
	config?: PipSnapConfig;
}): PipPosition {
	const config = params.config ?? DEFAULT_PIP_SNAP_CONFIG;
	const padding = resolvePipSnapPadding(config, params.insets);
	const maxX = Math.max(
		padding.left,
		params.bounds.width - params.pipDimensions.widthPx - padding.right,
	);
	const maxY = Math.max(
		padding.top,
		params.bounds.height - params.pipDimensions.heightPx - padding.bottom,
	);
	return {
		x: Math.min(Math.max(params.position.x, padding.left), maxX),
		y: Math.min(Math.max(params.position.y, padding.top), maxY),
	};
}

export function resolvePipDefaultPosition(params: {
	bounds: PipBounds;
	pipDimensions: PipDimensions;
	insets?: PipSnapInsets;
	config?: PipSnapConfig;
}): PipPosition {
	const config = params.config ?? DEFAULT_PIP_SNAP_CONFIG;
	const padding = resolvePipSnapPadding(config, params.insets);
	const maxX = Math.max(
		padding.left,
		params.bounds.width - params.pipDimensions.widthPx - padding.right,
	);
	const maxY = Math.max(
		padding.top,
		params.bounds.height - params.pipDimensions.heightPx - padding.bottom,
	);
	const anchor = config.defaultAnchor ?? "bottom-left";

	switch (anchor) {
		case "top-right":
			return { x: maxX, y: padding.top };
		case "top-left":
			return { x: padding.left, y: padding.top };
		case "bottom-right":
			return { x: maxX, y: maxY };
		default:
			return { x: padding.left, y: maxY };
	}
}

export function snapPipPosition(params: {
	position: PipPosition;
	bounds: PipBounds;
	pipDimensions: PipDimensions;
	insets?: PipSnapInsets;
	config?: PipSnapConfig;
	snapRadiusPx?: number;
}): PipPosition {
	const config = params.config ?? DEFAULT_PIP_SNAP_CONFIG;
	const snapRadius = params.snapRadiusPx ?? config.cornerSnapRadiusPx;
	const padding = resolvePipSnapPadding(config, params.insets);
	const maxX = Math.max(
		padding.left,
		params.bounds.width - params.pipDimensions.widthPx - padding.right,
	);
	const maxY = Math.max(
		padding.top,
		params.bounds.height - params.pipDimensions.heightPx - padding.bottom,
	);
	const clamped = clampPipPosition({
		position: params.position,
		bounds: params.bounds,
		pipDimensions: params.pipDimensions,
		insets: params.insets,
		config,
	});

	const cornerTargets: PipPosition[] = [
		{ x: padding.left, y: padding.top },
		{ x: maxX, y: padding.top },
		{ x: padding.left, y: maxY },
		{ x: maxX, y: maxY },
	];

	let nearest = cornerTargets[0];
	let nearestDistance = distance(clamped, nearest);
	for (const target of cornerTargets.slice(1)) {
		const nextDistance = distance(clamped, target);
		if (nextDistance < nearestDistance) {
			nearest = target;
			nearestDistance = nextDistance;
		}
	}

	if (nearestDistance <= snapRadius) {
		return nearest;
	}

	const edgeTargets: PipPosition[] = [
		{ x: clamped.x, y: padding.top },
		{ x: clamped.x, y: maxY },
		{ x: padding.left, y: clamped.y },
		{ x: maxX, y: clamped.y },
	];

	for (const target of edgeTargets) {
		if (distance(clamped, target) <= snapRadius) {
			return target;
		}
	}

	return clamped;
}
